import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import type { ProjectCreate, ProjectUpdate } from '@/lib/schemas';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';

// ============================================================================
// Query Hooks
// ============================================================================

export function useProjects(
  filters?: {
    status?: string;
    search?: string;
    workflow_stage?: string;
    category?: string;
    has_action_needed?: boolean;
    updated_within_days?: number;
    exclude_active_proposals?: boolean;  // Phase 9.6: Exclude proposals in Active tab from Archived
    is_template?: boolean;
    has_project_number?: boolean;  // Phase 7.5.19: Filter by project_number presence
    is_archived?: boolean;  // V108: Soft-delete archive filter
    include_archived?: boolean;  // #150: include BOTH archived + active (Proposals → Archived)
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  },
  options?: {
    keepPreviousData?: boolean;
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: ['projects', filters],
    queryFn: () => api.projects.list(filters),
    placeholderData: options?.keepPreviousData ? (previousData) => previousData : undefined,
    enabled: options?.enabled ?? true,
  });
}

/**
 * @deprecated Use useProjectById instead. This hook uses queryKey ['projects', id]
 * which gets caught by invalidateProjectListQueries(), causing unintended refetches
 * that can overwrite optimistic updates on the project detail page.
 */
export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => api.projects.get(id!),
    enabled: !!id,
  });
}

export function useProjectById(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projects', 'detail', projectId],
    queryFn: async () => {
      return await api.projects.get(projectId!);
    },
    enabled: !!projectId,
    staleTime: 30_000, // 30s — prevent aggressive refetch on remount
  });
}

// Alias for backward compatibility during migration
export const useProjectByNumber = useProjectById;

export function useNextProjectNumber() {
  return useQuery({
    queryKey: ['projects', 'next-number'],
    queryFn: () => api.projects.getNextNumber(),
    staleTime: 0,  // Always fetch fresh to avoid stale numbers
  });
}

// Phase 9.6c: Preview next project number for a category (doesn't increment counter)
export function useNextProjectNumberPreview(categoryId: string | undefined) {
  return useQuery({
    queryKey: ['projects', 'next-number-preview', categoryId],
    queryFn: () => api.projects.getNextNumberPreview(categoryId!),
    enabled: !!categoryId,
    staleTime: 30000, // Cache preview for 30 seconds
    select: (data) => data.next_number,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProjectCreate) => api.projects.create(data),
    onSuccess: async (response) => {
      const newProject = response?.project || response;
      // Update list cache directly from response — immediate UI update
      queryClient.setQueriesData<any>(
        { predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'projects' && key[1] !== 'detail' && key.length <= 2;
        }},
        (old) => {
          if (!old?.projects) return old;
          return {
            ...old,
            projects: [...old.projects, newProject],
            total: (old.total ?? 0) + 1,
          };
        }
      );
      // NOTE: ProjectForm.tsx also does invalidateQueries BEFORE navigation
      // for full eventual consistency
      // v0.17.16: when the renewal did not save, the caller shows a warning
      // instead; a green success next to it would contradict it.
      if (!response?.renewal_warning) toast.success('Project created successfully');
      return response.project;
    },
    onError: (error: any) => {
      console.log('[useCreateProject] onError called with:', {status: error?.status, message: error?.message, data: error?.data});
      if (error.status === 409) {
        const suggestion = error.data?.suggestion;
        const toastMessage = `Project number already exists${suggestion ? `. Try ${suggestion}?` : ''}`;
        console.log('[useCreateProject] Showing 409 toast:', toastMessage);
        toast.error(toastMessage);
      } else {
        const toastMessage = error.message || 'Failed to create project';
        console.log('[useCreateProject] Showing generic error toast:', toastMessage);
        toast.error(toastMessage);
      }
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProjectUpdate }) =>
      api.projects.update(id, data),
    onSuccess: async (response, variables) => {
      const project = response.project;
      // Update detail cache directly from response — immediate, no stale read risk
      queryClient.setQueryData(['projects', 'detail', variables.id], (old: any) => {
        if (!old?.project) return { project };
        return { ...old, project: { ...old.project, ...project } };
      });
      // Also update the legacy query key format
      queryClient.setQueryData(['projects', variables.id], (old: any) => {
        if (!old?.project) return { project };
        return { ...old, project: { ...old.project, ...project } };
      });
      // Invalidate list queries for the sidebar/list views
      invalidateProjectListQueries(queryClient);
      toast.success('Project updated successfully');
      return project;
    },
    onError: (error: any) => {
      if (error.status === 409) {
        toast.error('Project number already in use');
      } else {
        toast.error(error.message || 'Failed to update project');
      }
    },
  });
}

export function useUpdateProjectInline() {
  const queryClient = useQueryClient();

  return useMutation({
    // Add mutation key to track related mutations
    mutationKey: ['updateProjectInline'],

    mutationFn: ({ projectId, data }: {
      projectId: string;
      data: Partial<ProjectUpdate>
    }) => api.projects.update(projectId, data),

    // PATTERN 3: True Optimistic Update (for inline editing)
    // Update cache BEFORE server confirms for instant UI feedback
    onMutate: async ({ projectId, data }) => {
      // CRITICAL: Do optimistic update FIRST (synchronously) before any await
      // This ensures the UI updates immediately when onBlur sets isEditingAddress=false
      // If we await first, the component re-renders with old data before optimistic update

      // Snapshot the previous value for rollback
      const previous = queryClient.getQueryData(['projects', 'detail', projectId]);

      // If updating organization_id, look up client_name from ALL organizations caches
      // This prevents showing stale name while waiting for server response
      let optimisticData = { ...data };
      if (data.organization_id !== undefined) {
        // First check main organizations cache
        const organizationsData = queryClient.getQueryData(['organizations']) as any;
        const organizations = organizationsData?.organizations ?? [];
        let selectedOrg = organizations.find((org: any) => org.id === data.organization_id);

        // If not found in main cache, search ALL organization query caches
        // (includes search caches, recent caches, filtered caches, etc.)
        if (!selectedOrg) {
          const allOrganizationQueries = queryClient.getQueriesData({ queryKey: ['organizations'] });
          for (const [_queryKey, queryData] of allOrganizationQueries) {
            const orgs = (queryData as any)?.organizations ?? [];
            selectedOrg = orgs.find((org: any) => org.id === data.organization_id);
            if (selectedOrg) break; // Found it!
          }
        }

        // If found in any cache, update client_name optimistically
        if (selectedOrg) {
          optimisticData.client_name = selectedOrg.name;
        }
        // If not found in any cache, don't update client_name
        // Let the server response handle it to avoid showing "No client"
      }

      // Phase 7.5.5: If updating client_contact_id, look up client_contact_name from contacts cache
      // This prevents showing stale contact name while waiting for server response
      if (data.client_contact_id !== undefined) {
        if (data.client_contact_id === null) {
          // User selected "No contact" - clear the name
          optimisticData.client_contact_name = null;
        } else {
          // Get current project to find its organization_id (needed to query contacts)
          const currentProject = queryClient.getQueryData(['projects', 'detail', projectId]) as any;
          const orgId = currentProject?.project?.organization_id;

          if (orgId) {
            // Look up contacts cache for this organization
            const contactsData = queryClient.getQueryData(['organization-contacts', orgId]) as any;
            const contacts = contactsData?.contacts ?? [];
            const selectedContact = contacts.find((contact: any) => contact.id === data.client_contact_id);

            // If found in cache, update client_contact_name optimistically
            if (selectedContact) {
              optimisticData.client_contact_name = selectedContact.name;
            }
            // If not found in cache, don't update client_contact_name
            // Let the server response handle it to avoid showing "No contact"
          }
        }
      }

      // Optimistically update cache (synchronous - happens before component re-renders)
      queryClient.setQueryData(['projects', 'detail', projectId], (old: any) => {
        if (!old?.project) return old;
        return {
          ...old,
          project: { ...old.project, ...optimisticData }
        };
      });

      // Cancel any outgoing refetches AFTER optimistic update (async is fine here)
      await queryClient.cancelQueries({ queryKey: ['projects', 'detail', projectId] });

      // Return context for rollback
      return { previous, projectId };
    },

    // Rollback on error
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['projects', 'detail', context.projectId],
          context.previous
        );
      }
      toast.error('Failed to update project');
    },

    // Update cache from server response (authoritative) + show toast
    onSuccess: (response, variables) => {
      // The optimistic update in onMutate may not apply before the component
      // re-renders (it runs in a microtask after setIsEditing(false)).
      // This second setQueryData from the server response ensures the cache
      // is always correct, regardless of onMutate timing.
      const serverProject = response?.project;
      if (serverProject) {
        queryClient.setQueryData(
          ['projects', 'detail', variables.projectId],
          (old: any) => {
            if (!old?.project) return old;
            return { ...old, project: { ...old.project, ...serverProject } };
          }
        );
      }
      toast.success('Project updated');
    },

    // Sync with server AFTER mutation is fully settled (not just successful)
    // Use onSettled instead of onSuccess to ensure mutation is completely done
    onSettled: (_, __, variables) => {
      // CRITICAL FIX: Only invalidate if no other inline mutations are pending
      // This prevents race conditions where first mutation's refetch overwrites
      // second mutation's optimistic update with stale server data
      //
      // Race condition timeline without this check:
      // 1. Mutation A fires → server call → success → invalidates → refetch starts
      // 2. Mutation B fires → optimistic update
      // 3. Mutation A's refetch completes → overwrites B's optimistic update!
      // 4. Mutation B completes → but UI already reverted
      //
      // Solution: Only invalidate when this is the LAST mutation
      // Check in onSettled (not onSuccess) to ensure mutation is fully complete
      const otherMutationsInProgress = queryClient.isMutating({ mutationKey: ['updateProjectInline'] });

      // Theme 5 (mutation reliability): React Query v5 STILL counts THIS mutation
      // in isMutating() while onSettled runs — it's only removed afterward. The
      // old `=== 0` guard therefore evaluated false even for a lone inline project
      // edit, so the post-settle list/sidebar invalidation NEVER ran for a single
      // edit and the list went stale until a manual refresh. The correct test is
      // `<= 1`: allow when this settling mutation is the only one in flight; suppress
      // only when ANOTHER updateProjectInline is genuinely still pending. Same proven
      // fix and rationale as useInvoices.ts:714 (v0.14.4 R5 silent-loss kill).
      if (otherMutationsInProgress <= 1) { // <= 1 = this settling mutation is the only one
        // CRITICAL: Only invalidate LIST queries, not DETAIL query
        // The detail query is already correct from optimistic update
        // Invalidating it causes a refetch that can overwrite subsequent optimistic updates
        //
        // Use exact:true to match ONLY ['projects'] (list view)
        // This prevents matching ['projects', 'detail', X] (detail views)
        queryClient.invalidateQueries({ queryKey: ['projects'], exact: true });

        // Also invalidate filtered list queries: ['projects', filters]
        // But exclude detail queries: ['projects', 'detail', X]
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return key[0] === 'projects' && key[1] !== 'detail';
          }
        });

        // Phase 8.3: If client_contact_id was updated, also invalidate project-scopes
        // The ProposalPdfDownload component derives hasClientContact from scopes data
        // so we need to refetch scopes when contact changes
        if (variables.data.client_contact_id !== undefined) {
          queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
        }
      }
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) => api.projects.delete(projectId),
    onMutate: async (projectId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['projects'] });

      // Snapshot previous value
      const previousProjects = queryClient.getQueriesData({ queryKey: ['projects'] });

      // Optimistically update all project queries
      queryClient.setQueriesData<any>(
        { queryKey: ['projects'] },
        (old) => {
          if (!old) return old;
          // Only update list queries that have a projects array (not detail queries)
          if (!old.projects) return old;
          return {
            ...old,
            projects: old.projects.filter((p: any) => p.id !== projectId),
            total: old.total - 1, // Update total count for pagination
          };
        }
      );

      return { previousProjects };
    },
    onError: (error: any, projectId, context) => {
      // Rollback on error
      if (context?.previousProjects) {
        context.previousProjects.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error(error.message || 'Failed to archive project');
    },
    onSuccess: () => {
      // Invalidate project list queries to refetch server state after archival
      // This ensures navigation to /projects works correctly (page can load with fresh data)
      // Exclude detail queries to prevent stale reads overwriting other projects' data
      invalidateProjectListQueries(queryClient);
      toast.success('Project archived successfully');
    },
  });
}

export function useRestoreProject() {
  const queryClient = useQueryClient();

  // #150: `entityLabel` lets callers that know the entity type (the detail page,
  // or a Proposals-list row) get an accurate toast — the list view shows mixed
  // entities so it passes the per-row label.
  return useMutation({
    mutationFn: ({ id }: { id: string; entityLabel?: string }) => api.projects.restore(id),
    onSuccess: (_data, variables) => {
      invalidateProjectListQueries(queryClient);
      // #150: also refresh the single-project detail query so the archived banner
      // on the detail page clears immediately on Restore, not just on reload.
      queryClient.invalidateQueries({ queryKey: ['projects', 'detail', variables.id] });
      toast.success(`${variables.entityLabel ?? 'Item'} restored successfully`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to restore');
    },
  });
}

// ============================================================================
// Phase 7 Batch 7.3: Proposal Workflow Hooks
// ============================================================================

export function useDuplicateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      return api.projects.duplicate(projectId);
    },
    onSuccess: (data) => {
      const newProject = data.project;
      const itemsCopied = data.items_copied || 0;
      const scopesCopied = data.scopes_copied || 0;

      // Invalidate project list queries only — don't overwrite detail cache
      invalidateProjectListQueries(queryClient);

      toast.success(`Project duplicated successfully with ${itemsCopied} items and ${scopesCopied} scopes`);

      return newProject;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to duplicate project');
    },
  });
}

// Phase 7.5.10: Mark proposal as sent to client
export function useSendProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      return api.projects.sendProposal(projectId);
    },
    onSuccess: (data) => {
      const project = data.project;

      // Update detail cache directly from response, then invalidate lists
      queryClient.setQueryData(['projects', 'detail', project.id], (old: any) => {
        if (!old?.project) return { project };
        return { ...old, project: { ...old.project, ...project } };
      });
      invalidateProjectListQueries(queryClient);

      toast.success('Proposal marked as sent');

      return project;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to mark proposal as sent');
    },
  });
}

// Phase 9.6: Mark proposal as declined/lost
export function useDeclineProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      // Use the generic update endpoint - backend handles setting proposal_declined_at
      return api.projects.update(projectId, { workflow_stage: 'proposal_declined' });
    },
    onSuccess: (data) => {
      const project = data.project;

      // Update detail cache directly from response, then invalidate lists
      queryClient.setQueryData(['projects', 'detail', project.id], (old: any) => {
        if (!old?.project) return { project };
        return { ...old, project: { ...old.project, ...project } };
      });
      invalidateProjectListQueries(queryClient);

      toast.success('Proposal marked as lost');

      return project;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to mark proposal as lost');
    },
  });
}

export function useCopyTemplate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (templateId: string) => {
      return api.projects.copyTemplate(templateId);
    },
    onSuccess: (data) => {
      const newTemplate = data.template;
      const itemsCopied = data.items_copied || 0;
      const scopesCopied = data.scopes_copied || 0;

      // Invalidate project list queries only — don't overwrite detail cache
      invalidateProjectListQueries(queryClient);

      toast.success(`Template copied successfully with ${itemsCopied} items and ${scopesCopied} scopes`);

      // Navigate to the new template
      navigate(`/projects/${newTemplate.id}`);

      return newTemplate;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to copy template');
    },
  });
}

export function useCreateFromTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, data }: {
      templateId: string;
      data: {
        organization_id: string;
        name?: string;
        address?: string;
        notes?: string;
        client_contact_id?: string;
        // 2026-09-13 template-category fix: see api.ts createFromTemplate.
        category?: string | null;
      }
    }) => api.projects.createFromTemplate(templateId, data),
    onSuccess: (response) => {
      invalidateProjectListQueries(queryClient);
      toast.success('Project created from template');
      return response.project;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create project from template');
    },
  });
}

export function useConvertToTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) => api.projects.convertToTemplate(projectId),
    onSuccess: (response) => {
      invalidateProjectListQueries(queryClient);
      const newTemplate = response.template;
      toast.success(`Template "${newTemplate.name}" created! Your original project remains unchanged.`);
      // Navigation handled by component (can navigate to new template if needed)
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save as template');
    },
  });
}
